/**
 *
 * @page 用户中心/订单管理/企业会员
 *
 **/
import { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Popover, Table, Menu, Image } from 'antd';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { DownOutlined } from '@ant-design/icons';
import CorporateMemberTable from './CorporateMemberTable';
import { getUserInfoVip } from '&/api/personCenter/orderManage';
import useStoreState from '&/utils/customHooks/useStoreState';
import ConsultationModal from './ConsultationModal';
import dftEntLogo from '&/assets/img/member/entlogo.png';
import moment from 'moment';
import './index.scss';

export default observer(function CorporateMembership() {
    const { userInfo, organization, remoteLogo } = useStoreState('appStore');
    const searchMap = {
        1: intl.get('userCenter.member'),
        2: intl.get('userCenter.renderingVouchers'),
        3: intl.get('userCenter.projectDurationPackage'),
    };

    const [currentType, setCurrentType] = useState(1);

    const handleClickMenu = ({ key }) => {
        setCurrentType(key);
    };

    const NOT_OPEN = -1, // 自定义未开通的状态码
        EMPTY = -2;
    const entVipStatusMap = {
        [NOT_OPEN]: intl.get('userCenter.notOpen'),
        0: intl.get('userCenter.opened'),
        1: intl.get('userCenter.stale'),
    };

    const isVipEnt = userInfo.vipEntType === 'vip_ent_unite';
    const IS_FREE =
        !userInfo.vipEntType || userInfo.vipEntType === 'vip_ent_free';

    const VIP_DATA_DEFAULT = {
        isVipEnt,
        vipName: '企业联合会员',
        status: IS_FREE ? NOT_OPEN : EMPTY,
        entName: organization.name,
    };

    const [entData, setEntData] = useState(VIP_DATA_DEFAULT);

    const getPopContent = () => {
        const popColumns = [
            {
                title: intl.get('userCenter.distributionCycle'),
                dataIndex: 'distributionCycle',
            },
            {
                title: intl.get('userCenter.distributionTime'),
                dataIndex: 'distributionTime',
            },
            { title: intl.get('userCenter.issued'), dataIndex: 'issued' },
            { title: intl.get('collection.status'), dataIndex: 'status' },
        ];
        const popData = entData.couponGrantCycle?.map(item => {
            return {
                distributionCycle: intl.get('userCenter.cycleNum', {
                    num: item.sort,
                }),
                distributionTime: item.time,
                issued: item.count,
                status:
                    item.status === 1
                        ? intl.get('userCenter.isGranted')
                        : intl.get('userCenter.unissued'),
            };
        });

        return (
            <Table
                columns={popColumns}
                dataSource={popData}
                pagination={false}
                border
            />
        );
    };

    const queryEntDetail = async () => {
        try {
            const entId = organization.enterpriseId;

            const { data } = await getUserInfoVip(entId);
            if (!data) {
                setEntData(VIP_DATA_DEFAULT);
                return;
            }
            let nextCouponDate = '';
            if (data.couponGrantCycle.length) {
                const nextData = data.couponGrantCycle.find(
                    item => item.status === 2
                );
                nextCouponDate = nextData.time;
            }
            setEntData({
                ...data,
                nextCouponDate,
                isVipEnt,
            });
        } catch {}
    };

    useEffect(() => {
        queryEntDetail();
    }, []);

    const getUsageRecord = () => {
        const popColumns = [
            {
                title: intl.get('userCenter.usageTime'),
                dataIndex: 'usageTime',
            },
            {
                title: intl.get('userCenter.user'),
                dataIndex: 'user',
                align: 'center',
            },
            {
                title: intl.get('userCenter.usageProjectDuration'),
                dataIndex: 'duration',
                align: 'center',
            },
        ];
        const popData = entData.durationRecord?.map(item => {
            return {
                usageTime: moment(item.createDate).format('YYYY-MM-DD'),
                user: item.username,
                duration: item.duration,
            };
        });
        return (
            <Table
                columns={popColumns}
                dataSource={popData}
                pagination={false}
                border
            />
        );
    };

    const menu = (
        <Menu onClick={handleClickMenu}>
            <Menu.Item key="1">{intl.get('userCenter.member')}</Menu.Item>
            <Menu.Item key="2">
                {intl.get('userCenter.renderingVouchers')}
            </Menu.Item>
            <Menu.Item key="3">
                {intl.get('userCenter.projectDurationPackage')}
            </Menu.Item>
        </Menu>
    );

    const memberInformationList = [
        {
            label: intl.get('userCenter.versionInformation'),
            value: entData.vipName,
        },
        {
            label: intl.get('userCenter.versionStatus'),
            value: entVipStatusMap[entData.status],
        },
        {
            label: intl.get('userCenter.designRights'),
            value: intl.getHTML('userCenter.assignedAccount', {
                num: entData.surplusNum,
                total: entData.totalNum,
            }),
            subValue: intl.getHTML('userCenter.renderingVoucher', {
                num: entData.surplusCouponNum,
                total: entData.totalCouponNum,
            }),
            subValueTip: intl.get('userCenter.nextCouponIssuance', {
                date: entData.nextCouponDate,
            }),
        },
        {
            label: intl.get('userCenter.cloudServiceRights'),
            value: intl.getHTML('userCenter.projectDuration', {
                num: entData.surplusDuration,
                total: entData.totalDuration,
            }),
            getPpopver: getUsageRecord,
        },
        {
            label: intl.get('userCenter.memberExpirationDate'),
            value: entData.expireDate,
        },
    ];

    const consultationModalRef = useRef();
    const his = useHistory();

    return (
        <div className="corporate-membership">
            <div className="member-information">
                <div className="member-information-header">
                    <div className="member-name">
                        <Image
                            src={remoteLogo || dftEntLogo}
                            fallback={dftEntLogo}
                            alt="Logo"
                            width="2.5rem"
                            height="2.5rem"
                            preview={false}
                        />
                        <h3>{entData.entName}</h3>
                    </div>
                    <Button
                        type="primary"
                        onClick={() => {
                            IS_FREE
                                ? his.push('/pricing-page?index=2')
                                : consultationModalRef.current.show();
                        }}>
                        {intl.get(
                            IS_FREE
                                ? 'commonLandingPage.businessCooperation.consultNow'
                                : 'userCenter.consultationRenewal'
                        )}
                    </Button>
                </div>
                <ul className="member-information-list">
                    {memberInformationList.map((item, index) => {
                        let value = item.value;
                        if (index > 1 && !entData.isVipEnt) {
                            value = '-';
                        }
                        return (
                            <li className="member-information-item" key={index}>
                                <h4 className="item-label">{item.label}</h4>
                                <p className="item-value">
                                    {value}
                                    {entData.isVipEnt && item.getPpopver && (
                                        <Popover
                                            overlayClassName="sub-value-tip-pop"
                                            placement="right"
                                            content={item.getPpopver?.(item)}>
                                            <i className="filled icon-question-cirlce2" />
                                        </Popover>
                                    )}
                                </p>
                                {entData.isVipEnt && item.subValue && (
                                    <p className="item-sub-value">
                                        {item.subValue}
                                    </p>
                                )}
                                {entData.isVipEnt && item.subValueTip && (
                                    <span className="sub-value-tip">
                                        {item.subValueTip}
                                        <Popover
                                            overlayClassName="sub-value-tip-pop"
                                            placement="right"
                                            content={getPopContent(item)}>
                                            <i className="filled icon-question-cirlce2" />
                                        </Popover>
                                    </span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </div>
            <div className="purchase-record">
                <div className="purchase-record-header">
                    <h3>{intl.get('userCenter.purchaseRecord')}</h3>
                    <Dropdown overlay={menu}>
                        <div className="blue-btn">
                            <span>
                                {intl.get('userCenter.type')}:
                                {searchMap[currentType]}
                            </span>
                            <DownOutlined />
                        </div>
                    </Dropdown>
                </div>
                <CorporateMemberTable type={currentType} />
            </div>
            <ConsultationModal ref={consultationModalRef} />
        </div>
    );
});
